/**
 * Client settings API service
 */

import { apiClient, APIResponse } from './client';

// Client Settings Types
export interface AutoConfirmSettings {
  enabled: boolean;
  delayMinutes: number;
}

export interface AutoContractAnalysisSettings {
  enabled: boolean;
  lookbackDays: number;
  retryAttempts: number;
  retryDelayMinutes: number;
}

export interface AutomationSettings {
  dataSharing: boolean;
  autoConfirmMatched: AutoConfirmSettings;
  autoCartaInstruccion: boolean;
  autoConfirmDisputed: AutoConfirmSettings;
  autoExpiryResponse: boolean;
  autoContractAnalysis?: AutoContractAnalysisSettings;
}

export interface EmailAlertSettings {
  enabled: boolean;
  emails: string[];
}

export interface SmsAlertSettings {
  enabled: boolean;
  phones: string[];
}

export interface AlertSettings {
  emailConfirmedTrades: EmailAlertSettings;
  emailDisputedTrades: EmailAlertSettings;
  emailAutoSettlement: EmailAlertSettings;
  smsConfirmedTrades: SmsAlertSettings;
  smsDisputedTrades: SmsAlertSettings;
  smsAutoSettlement: SmsAlertSettings;
}

export interface PreferencesSettings {
  language: string;
  timezone: string;
  dateFormat: string;
  numberFormat: string;
}

export interface ValuationPreferences {
  forwardRateSource: 'cirp' | 'fwd_points';
  discountingMethod: 'two_leg' | 'net_pnl';
  fwdPointsPrice: 'market' | 'mid' | 'bid' | 'ask';
  valuationCurrency: string;
  asiaticoMethod: 'blended' | 'single_rate';
  valuationDateMode: 'system_date' | 'previous_business_day' | 'end_of_previous_month';
  marketDataDateMode: 'system_date' | 'valuation_date' | 'first_business_day';
  forwardStartingEligibility: 'confirmed_only' | 'include_provisional';
  forwardStartingMethod: 'single_rate' | 'blended';
  splitMtmLevel: 'parent' | 'split';
  ufSpotDateMode: 'inherit' | 'furthest_known' | 'execution_date' | 'as_of_date' | 'first_business_day';
  spotDateMode: 'curve_date' | 'execution_date' | 'as_of_date' | 'first_business_day';
}

export const DEFAULT_VALUATION_PREFERENCES: ValuationPreferences = {
  forwardRateSource: 'cirp',
  discountingMethod: 'net_pnl',
  fwdPointsPrice: 'market',
  valuationCurrency: 'CLP',
  asiaticoMethod: 'blended',
  valuationDateMode: 'system_date',
  marketDataDateMode: 'system_date',
  forwardStartingEligibility: 'confirmed_only',
  forwardStartingMethod: 'single_rate',
  splitMtmLevel: 'parent',
  ufSpotDateMode: 'inherit',
  spotDateMode: 'curve_date',
};

export interface ExpiryToleranceEntry {
  currency: string;
  tolerance: number;
}

export interface ExpiryToleranceSettings {
  currencyTolerances: ExpiryToleranceEntry[];
  defaultTolerance: number;
  lastUpdatedAt?: string;
  lastUpdatedBy?: string;
}

export interface ExpiryToleranceSettingsUpdate {
  currencyTolerances: ExpiryToleranceEntry[];
  defaultTolerance: number;
}

export const DEFAULT_EXPIRY_TOLERANCES: ExpiryToleranceSettings = {
  currencyTolerances: [],
  defaultTolerance: 0.01,
};

export interface ExpiryEmailConfig {
  counterpartyExpiryEmails: Record<string, string>;
  expiryEmailCc: string;
}

export interface ExpiryEmailConfigUpdate {
  counterpartyExpiryEmails: Record<string, string>;
  expiryEmailCc: string;
}

export interface ClientSettings {
  id?: string;
  createdAt?: string;
  lastUpdatedAt?: string;
  lastUpdatedBy?: string;
  automation: AutomationSettings;
  alerts: AlertSettings;
  preferences: PreferencesSettings;
  valuation: ValuationPreferences;
}

export interface ClientSettingsUpdate {
  automation?: Partial<AutomationSettings>;
  alerts?: Partial<AlertSettings>;
  preferences?: Partial<PreferencesSettings>;
  valuation?: Partial<ValuationPreferences>;
}

// Bank Account Types
export interface BankAccount {
  id?: string;
  createdAt?: string;
  lastUpdatedAt?: string;
  lastUpdatedBy?: string;
  active: boolean;
  accountName: string;
  bankName: string;
  swiftCode: string;
  accountCurrency: string;
  accountNumber: string;
  isDefault: boolean;
  accountType?: 'nostro' | 'vostro';
  counterpartyId?: string;
  counterpartyName?: string;
}

export interface BankAccountCreate {
  accountName: string;
  bankName: string;
  swiftCode: string;
  accountCurrency: string;
  accountNumber: string;
  isDefault: boolean;
  accountType?: 'nostro' | 'vostro';
  counterpartyId?: string;
  counterpartyName?: string;
}

export interface BankAccountUpdate {
  active?: boolean;
  accountName?: string;
  bankName?: string;
  swiftCode?: string;
  accountCurrency?: string;
  accountNumber?: string;
  isDefault?: boolean;
  accountType?: 'nostro' | 'vostro';
  counterpartyId?: string;
  counterpartyName?: string;
}

// Data Mapping Types
export interface FieldTransformation {
  type: string;
  params: Record<string, any>;
}

export interface FieldMapping {
  sourceField: string;
  targetField: string;
  transformation: FieldTransformation;
}

export interface ExpectedField {
  name: string;
  type: string;
  required: boolean;
  format?: string;
  enumValues?: string[];
}

export interface DataMapping {
  id?: string;
  createdAt?: string;
  lastUpdatedAt?: string;
  lastUpdatedBy?: string;
  name: string;
  description: string;
  fileType: string;
  isDefault: boolean;
  fieldMappings: FieldMapping[];
  expectedFields: ExpectedField[];
  lastUsedAt?: string;
  usageCount: number;
}

export interface DataMappingCreate {
  name: string;
  description: string;
  fileType: string;
  isDefault: boolean;
  fieldMappings: FieldMapping[];
  expectedFields: ExpectedField[];
}

export interface DataMappingUpdate {
  name?: string;
  description?: string;
  fileType?: string;
  isDefault?: boolean;
  fieldMappings?: FieldMapping[];
  expectedFields?: ExpectedField[];
}

// Trading Entity Types
export interface TradingEntity {
  id: string;
  name: string;
  taxId: string;
  parentId: string | null;
  isParent: boolean;
}

// FXGO Settings Types
export interface FXGOSpotRow {
  currencyPair: string;
  grouping1: string;
  grouping2: string;
}

export interface FXGOForwardRow {
  currencyPair: string;
  grouping1: string;
  grouping2: string;
}

export interface FXGOValueMappingEntry {
  from: string;
  to: string;
}

export interface FXGOCustomFieldMapping {
  sourceField: string;
  targetField: string;
  transformation: 'direct' | 'valueMapping';
  valueMapping: FXGOValueMappingEntry[];
}

export interface FXGOSettings {
  spotRows: FXGOSpotRow[];
  forwardRows: FXGOForwardRow[];
  customFieldMappings: FXGOCustomFieldMapping[];
  lastUpdatedAt?: string;
  lastUpdatedBy?: string;
}

// Entity Alias Types (KAN-277)
export interface EntityAlias {
  id: string;
  entityType: 'bank' | 'organization';
  entityId: string;
  realName: string;
  alias: string;
  createdAt?: string;
  createdBy?: string;
}

export interface EntityAliasCreate {
  entityType: 'bank' | 'organization';
  entityId: string;
  realName: string;
  alias: string;
}

export interface EntityAliasUpdate {
  alias: string;
}


class ClientService {
  // ========== Trading Entities Methods ==========

  /**
   * Get all trading entities accessible to a client (client + children)
   */
  async getTradingEntities(clientId: string): Promise<APIResponse<TradingEntity[]>> {
    return apiClient.get(`/api/v1/clients/${clientId}/trading-entities`);
  }

  // ========== Counterparty Names Methods ==========

  /**
   * Get counterparty names from the client's counterpartyMappings.
   * Optionally filter by type: 'fx', 'commodity', or omit for all.
   */
  async getCounterpartyNames(
    clientId: string,
    type?: 'fx' | 'commodity'
  ): Promise<APIResponse<{ counterpartyName: string; bankId: string }[]>> {
    const params = type ? `?type=${type}` : '';
    return apiClient.get(`/api/v1/clients/${clientId}/counterparty-names${params}`);
  }

  // ========== Client Settings Methods ==========

  /**
   * Get client settings configuration
   */
  async getClientSettings(clientId: string): Promise<APIResponse<ClientSettings>> {
    return apiClient.get(`/api/v1/clients/${clientId}/settings`);
  }

  /**
   * Update client settings configuration
   */
  async updateClientSettings(clientId: string, settings: ClientSettingsUpdate): Promise<APIResponse<ClientSettings>> {
    return apiClient.put(`/api/v1/clients/${clientId}/settings`, settings);
  }

  // ========== Bank Account Methods ==========

  /**
   * Get all bank accounts for a client
   */
  async getBankAccounts(clientId: string): Promise<APIResponse<BankAccount[]>> {
    return apiClient.get(`/api/v1/clients/${clientId}/bank-accounts`);
  }

  /**
   * Get specific bank account
   */
  async getBankAccount(clientId: string, accountId: string): Promise<APIResponse<BankAccount>> {
    return apiClient.get(`/api/v1/clients/${clientId}/bank-accounts/${accountId}`);
  }

  /**
   * Create a new bank account
   */
  async createBankAccount(clientId: string, account: BankAccountCreate): Promise<APIResponse<BankAccount>> {
    return apiClient.post(`/api/v1/clients/${clientId}/bank-accounts`, account);
  }

  /**
   * Update bank account
   */
  async updateBankAccount(clientId: string, accountId: string, updates: BankAccountUpdate): Promise<APIResponse<BankAccount>> {
    return apiClient.put(`/api/v1/clients/${clientId}/bank-accounts/${accountId}`, updates);
  }

  /**
   * Delete bank account
   */
  async deleteBankAccount(clientId: string, accountId: string): Promise<APIResponse<{}>> {
    return apiClient.delete(`/api/v1/clients/${clientId}/bank-accounts/${accountId}`);
  }

  // ========== Data Mapping Methods ==========

  /**
   * Get all data mappings for a client
   */
  async getDataMappings(clientId: string): Promise<APIResponse<DataMapping[]>> {
    return apiClient.get(`/api/v1/clients/${clientId}/data-mappings`);
  }

  /**
   * Get specific data mapping
   */
  async getDataMapping(clientId: string, mappingId: string): Promise<APIResponse<DataMapping>> {
    return apiClient.get(`/api/v1/clients/${clientId}/data-mappings/${mappingId}`);
  }

  /**
   * Create a new data mapping
   */
  async createDataMapping(clientId: string, mapping: DataMappingCreate): Promise<APIResponse<DataMapping>> {
    return apiClient.post(`/api/v1/clients/${clientId}/data-mappings`, mapping);
  }

  /**
   * Update data mapping
   */
  async updateDataMapping(clientId: string, mappingId: string, updates: DataMappingUpdate): Promise<APIResponse<DataMapping>> {
    return apiClient.put(`/api/v1/clients/${clientId}/data-mappings/${mappingId}`, updates);
  }

  /**
   * Delete data mapping
   */
  async deleteDataMapping(clientId: string, mappingId: string): Promise<APIResponse<{}>> {
    return apiClient.delete(`/api/v1/clients/${clientId}/data-mappings/${mappingId}`);
  }

  // ========== FXGO Settings Methods ==========

  /**
   * Get FXGO default settings for a client
   */
  async getFXGOSettings(clientId: string): Promise<APIResponse<FXGOSettings>> {
    return apiClient.get(`/api/v1/clients/${clientId}/settings/fxgo`);
  }

  /**
   * Update FXGO default settings for a client
   */
  async updateFXGOSettings(clientId: string, settings: Partial<FXGOSettings>): Promise<APIResponse<FXGOSettings>> {
    return apiClient.put(`/api/v1/clients/${clientId}/settings/fxgo`, settings);
  }

  // ========== Valuation Preferences Methods ==========

  async getValuationPreferences(clientId: string): Promise<APIResponse<ValuationPreferences>> {
    return apiClient.get(`/api/v1/clients/${clientId}/settings/valuation`);
  }

  async updateValuationPreferences(clientId: string, prefs: ValuationPreferences): Promise<APIResponse<ValuationPreferences>> {
    return apiClient.put(`/api/v1/clients/${clientId}/settings/valuation`, prefs);
  }

  // ========== Expiry Tolerance Settings Methods ==========

  async getExpiryTolerances(clientId: string): Promise<APIResponse<ExpiryToleranceSettings>> {
    return apiClient.get(`/api/v1/clients/${clientId}/settings/expiry-tolerances`);
  }

  async updateExpiryTolerances(clientId: string, settings: ExpiryToleranceSettingsUpdate): Promise<APIResponse<ExpiryToleranceSettings>> {
    return apiClient.put(`/api/v1/clients/${clientId}/settings/expiry-tolerances`, settings);
  }

  // ========== Expiry Email Config Methods ==========

  async getExpiryEmailConfig(clientId: string): Promise<APIResponse<ExpiryEmailConfig>> {
    return apiClient.get(`/api/v1/clients/${clientId}/settings/expiry-email-config`);
  }

  async updateExpiryEmailConfig(clientId: string, config: ExpiryEmailConfigUpdate): Promise<APIResponse<ExpiryEmailConfig>> {
    return apiClient.put(`/api/v1/clients/${clientId}/settings/expiry-email-config`, config);
  }

  // ========== Expiry Accept Counterparty Methods ==========

  async acceptCounterpartyValues(clientId: string, tradeIds: string[]): Promise<APIResponse<{ updated: string[] }>> {
    return apiClient.post(`/api/v1/clients/${clientId}/expiry-accept-counterparty`, { tradeIds });
  }

  // ========== Contract Exceptions Methods (KAN-195) ==========

  async getContractExceptions(clientId: string, statusFilter: string = 'open'): Promise<APIResponse<any>> {
    return apiClient.get(`/api/v1/clients/${clientId}/contract-exceptions?status_filter=${statusFilter}`);
  }

  async resolveContractException(clientId: string, exceptionId: string, resolution: string, tradeId?: string): Promise<APIResponse<any>> {
    const params = new URLSearchParams({ resolution });
    if (tradeId) params.set('trade_id', tradeId);
    return apiClient.post(`/api/v1/clients/${clientId}/contract-exceptions/${exceptionId}/resolve?${params}`);
  }

  async deleteContractException(clientId: string, exceptionId: string): Promise<APIResponse<any>> {
    return apiClient.delete(`/api/v1/clients/${clientId}/contract-exceptions/${exceptionId}`);
  }

  // ========== Entity Aliases (KAN-277) ==========

  async listEntityAliases(clientId: string): Promise<APIResponse<EntityAlias[]>> {
    return apiClient.get(`/api/v1/clients/${clientId}/entity-aliases`);
  }

  async createEntityAlias(clientId: string, data: EntityAliasCreate): Promise<APIResponse<EntityAlias>> {
    return apiClient.post(`/api/v1/clients/${clientId}/entity-aliases`, data);
  }

  async updateEntityAlias(clientId: string, aliasId: string, data: EntityAliasUpdate): Promise<APIResponse<EntityAlias>> {
    return apiClient.put(`/api/v1/clients/${clientId}/entity-aliases/${aliasId}`, data);
  }

  async deleteEntityAlias(clientId: string, aliasId: string): Promise<APIResponse<any>> {
    return apiClient.delete(`/api/v1/clients/${clientId}/entity-aliases/${aliasId}`);
  }
}

export const clientService = new ClientService();